import React from "react";
import { cloneDeep } from "lodash";
import {
  ResultComparison,
  ScaleComponent,
  GelDragDrop,
} from "@learningblocks/storybook-components";
import "./style.scss";

const params = {
  "gelTheme": "DARK",
  "title": "Drag the band in each lane to predict the results for the controls:",
  "heading": "Predict the gel results:",
  "text": "Based on your understanding of PCR, drag the bands in each lane from the well to where you would expect to see them relative to the DNA ladder in lane 1. If you do not expect to see a band in a lane, drag the band from the well to the area below the gel to discard it.\n\nThe gel lanes include the DNA ladder, the sample with unknown extracted DNA from a patient, two positive controls (Control 1 and 2), and two negative controls (Control 3 and 4).\n\nControl 1 = Positive control: normal, full length, <i>GENE X</i>\n\nControl 2 = Positive control: mutated, shorter, <i>GENE X</i>\n\nControl 3 = Negative control: no DNA template\n\nControl 4 = Negative control: prokaryotic DNA template that does not contain <i>GENE X</i>",
  "gelRange": {
    "bottom": 80,
    "top": 1000
  },
  "content": [
    "heading",
    "text",
    "dragdrop"
  ],
  "gelTable": [
    {
      "colId": 0,
      "colType": "LABEL_COLUMN",
      "colItems": [
        {
          "cellId": 0,
          "cellType": "LABEL_CELL",
          "label": ""
        },
        {
          "cellId": 1,
          "cellType": "GEL_HOLDER",
          "cellItems": null,
          "label": ""
        },
        {
          "cellId": 2,
          "cellType": "GEL_PATH",
          "cellItems": [
            {
              "gelId": 0,
              "gelType": "LABEL",
              "height": 100,
              "opacity": 1,
              "value": 1000,
              "selected": false
            },
            {
              "gelId": 1,
              "gelType": "LABEL",
              "height": 100,
              "opacity": 1,
              "value": 700,
              "selected": false
            },
            {
              "gelId": 2,
              "gelType": "LABEL",
              "height": 100,
              "opacity": 1,
              "value": 500,
              "selected": false
            },
            {
              "gelId": 3,
              "gelType": "LABEL",
              "height": 100,
              "opacity": 1,
              "value": 300,
              "selected": false
            },
            {
              "gelId": 4,
              "gelType": "LABEL",
              "height": 100,
              "opacity": 1,
              "value": 100,
              "selected": false
            },
            {
              "gelId": 5,
              "gelType": "LABEL",
              "height": 100,
              "opacity": 1,
              "value": 200,
              "selected": false
            }
          ]
        },
        {
          "cellId": 3,
          "cellType": "TRASH_LABEL"
        }
      ]
    },
    {
      "colId": 1,
      "colType": "LADDER_COLUMN",
      "colItems": [
        {
          "cellId": 0,
          "cellType": "LABEL_CELL",
          "label": "DNA ladder"
        },
        {
          "cellId": 1,
          "cellType": "GEL_HOLDER",
          "cellItems": []
        },
        {
          "cellId": 2,
          "cellType": "GEL_PATH",
          "cellItems": [
            {
              "gelId": 0,
              "gelType": "FIXED",
              "height": 40,
              "opacity": 1,
              "value": 1000,
              "selected": false
            },
            {
              "gelId": 2,
              "gelType": "FIXED",
              "height": 40,
              "opacity": 1,
              "value": 700,
              "selected": false
            },
            {
              "gelId": 3,
              "gelType": "FIXED",
              "height": 80,
              "opacity": 1,
              "value": 500,
              "selected": false
            },
            {
              "gelId": 4,
              "gelType": "FIXED",
              "height": 40,
              "opacity": 1,
              "value": 300,
              "selected": false
            },
            {
              "gelId": 5,
              "gelType": "FIXED",
              "height": 40,
              "opacity": 1,
              "value": 200,
              "selected": false
            },
            {
              "gelId": 6,
              "gelType": "FIXED",
              "height": 40,
              "opacity": 1,
              "value": 100,
              "selected": false
            }
          ]
        },
        {
          "cellId": 3,
          "cellType": "TRASH_CAN",
          "cellItems": []
        }
      ]
    },
    {
      "colId": 2,
      "colType": "BAND_COLUMN",
      "colItems": [
        {
          "cellId": 0,
          "cellType": "LABEL_CELL",
          "label": "C1"
        },
        {
          "cellId": 1,
          "cellType": "GEL_HOLDER",
          "cellItems": [
            {
              "gelId": 0,
              "gelType": "DRAGGABLE",
              "height": 100,
              "opacity": 1,
              "value": 0,
              "selected": false
            }
          ]
        },
        {
          "cellId": 2,
          "cellType": "GEL_PATH",
          "cellItems": []
        },
        {
          "cellId": 3,
          "cellType": "TRASH_CAN",
          "cellItems": []
        }
      ]
    },
    {
      "colId": 3,
      "colType": "BAND_COLUMN",
      "colItems": [
        {
          "cellId": 0,
          "cellType": "LABEL_CELL",
          "label": "C2"
        },
        {
          "cellId": 1,
          "cellType": "GEL_HOLDER",
          "cellItems": [
            {
              "gelId": 0,
              "gelType": "DRAGGABLE",
              "height": 100,
              "opacity": 1,
              "value": 0,
              "selected": false
            }
          ]
        },
        {
          "cellId": 2,
          "cellType": "GEL_PATH",
          "cellItems": []
        },
        {
          "cellId": 3,
          "cellType": "TRASH_CAN",
          "cellItems": []
        }
      ]
    },
    {
      "colId": 4,
      "colType": "BAND_COLUMN",
      "colItems": [
        {
          "cellId": 0,
          "cellType": "LABEL_CELL",
          "label": "C3"
        },
        {
          "cellId": 1,
          "cellType": "GEL_HOLDER",
          "cellItems": [
            {
              "gelId": 0,
              "gelType": "DRAGGABLE",
              "height": 100,
              "opacity": 1,
              "value": 0,
              "selected": false
            }
          ]
        },
        {
          "cellId": 2,
          "cellType": "GEL_PATH",
          "cellItems": []
        },
        {
          "cellId": 3,
          "cellType": "TRASH_CAN",
          "cellItems": []
        }
      ]
    },
    {
      "colId": 5,
      "colType": "BAND_COLUMN",
      "colItems": [
        {
          "cellId": 0,
          "cellType": "LABEL_CELL",
          "label": "S"
        },
        {
          "cellId": 1,
          "cellType": "GEL_HOLDER",
          "cellItems": [
            {
              "gelId": 0,
              "gelType": "DRAGGABLE",
              "height": 100,
              "opacity": 1,
              "value": 0,
              "selected": false
            },
            {
              "gelId": 1,
              "gelType": "DRAGGABLE",
              "height": 100,
              "opacity": 1,
              "value": 0,
              "selected": false
            }
          ]
        },
        {
          "cellId": 2,
          "cellType": "GEL_PATH",
          "cellItems": []
        },
        {
          "cellId": 3,
          "cellType": "TRASH_CAN",
          "cellItems": []
        }
      ]
    }
  ]
};

export class ResultsComparisonGel extends React.Component {

  idealData = {
    "DNA A": {
      "S": [
        240,
        0
      ],
      "C1": [
        240
      ],
      "C2": [
        240
      ]
    },
    "DNA B": {
      "S": [
        240,
        0
      ],
      "C1": [
        240
      ],
      "C2": [
        240
      ]
    },
    "DNA C": {
      "S": [
        0,
        0
      ],
      "C1": [
        240
      ],
      "C2": [
        240
      ]
    }
  }

  getIdealLanes = (data) => {
    data.gelTable = data.gelTable.map((col) => {

      // Rearrange the gel_holder and gel_path props
      if(col.colItems[1].cellType === "GEL_HOLDER" && col.colItems[1].cellItems !== null && col.colItems[1].cellItems.length > 0){
        if(col.colItems[2].cellType === "GEL_PATH"){
          col.colItems[2].cellItems = col.colItems[1].cellItems;
          col.colItems[1].cellItems = [];
        }
      }

      // Insert the Ideal values
      col.colItems = col.colItems.map((item) => {
          if(item.cellType === "GEL_PATH" && item.cellItems !== null){
            item.cellItems = item.cellItems.filter((band, bandIndex)=>{
              let key = Object.keys(this.props.interactionProps.solutions[13].solutions)[0];
              if(this.idealData[key].hasOwnProperty(col.colItems[0].label)){
                band.value = this.idealData[key][col.colItems[0].label][bandIndex];
              }
              if(band.value !== 0){
                return band;
              }
            })
          }
          return item;
      });
      return col;
    });

    return data;
  }

  actualData = {
    "DNA A": {
      "S": [
        240,
        0
      ],
      "C1": [
        240
      ],
      "C2": [
        240
      ]
    },
    "DNA B": {
      "S": [
        240,
        0
      ],
      "C1": [
        240
      ],
      "C2": [
        240
      ]
    },
    "DNA C": {
      "S": [
        0,
        0
      ],
      "C1": [
        240
      ],
      "C2": [
        240
      ]
    }
  }

  getActualLanes = (data) => {

    let solutionIDs = {
      "S": 4,
      "C1": 1,
      "C2": 2,
      "C3": 3,
      "Extracted human DNA": 13
    }

    data.gelTable = data.gelTable.map((col) => {

      // Rearrange the gel_holder and gel_path props
      if(col.colItems[1].cellType === "GEL_HOLDER" && col.colItems[1].cellItems !== null && col.colItems[1].cellItems.length > 0){
        if(col.colItems[2].cellType === "GEL_PATH"){
          //col.colItems[2].cellItems = col.colItems[1].cellItems;
          col.colItems[1].cellItems = [];
        }
      }

      // If basic requirements for a result fufilled
      if(this.props.prepareGelBox &&
         this.props.runGel &&
         //this.props.visualizeGel &&
         this.props.runThermocycler &&
         this.props.loadDNALadder){

           // Insert the Actual values
           col.colItems = col.colItems.map((item) => {
               if(item.cellType === "GEL_PATH" && solutionIDs.hasOwnProperty(col.colItems[0].label)){

                 let solution = this.props.interactionProps.solutions[solutionIDs[col.colItems[0].label] - 1];
                 let thermocycler = this.props.interactionProps.thermocyclers[0];
                 let id = 0;

                  // Prerequisite conditions for a result
                  if(
                    solution.solutions["Taq DNA Polymerase"] >= 0.5 &&
                    solution.solutions["Buffer"] >= 2.5 &&
                    solution.solutions["dNTPs"] >= 0.5 &&
                    (
                      (
                        solution.solutions["Forward Primer"] > 0 &&
                        solution.solutions["Reverse Primer"] > 0
                      )
                      ||
                      (
                        solution.solutions["Primer Mix of Primers 1 and 8"] >= 1 //||
                        // solution.solutions["Primer Mix of Primers 2 and 5"] >= 1 ||
                        // solution.solutions["Primer Mix of Primers 3 and 4"] >= 1
                      )
                    )
                  ){
                    let opacity = 1;

                    // Too little primer
                    if(
                      solution.solutions["Forward Primer"] > 0 &&
                      solution.solutions["Forward Primer"] < 0.5 &&
                      solution.solutions["Reverse Primer"] > 0 &&
                      solution.solutions["Reverse Primer"] < 0.5
                    ){
                      opacity = 0.5;
                    }

                    // Too much buffer
                    if(solution.solutions["Buffer"] > 2.5){
                      opacity = 0.5;
                    }

                    // Band opacity based off the number of repeating cycles
                    if(thermocycler.sequence.length > 1 && thermocycler.sequence[1].hasOwnProperty('repeat')){
                      if(thermocycler.sequence[1].repeat > 0 && thermocycler.sequence[1].repeat < 21){
                        opacity = 0.2;
                      }
                      if(thermocycler.sequence[1].repeat > 20 && thermocycler.sequence[1].repeat < 26){
                        opacity = 0.5;
                      }
                      if(thermocycler.sequence[1].repeat > 25){
                        opacity = 1;
                      }
                    } else {
                      opacity = 0.2;
                    }


                    if(thermocycler.sequence.length > 1 && thermocycler.sequence[1].hasOwnProperty('subSteps')){
                      // Denaturing

                        // no bands
                        if(thermocycler.sequence[1].subSteps[0].temp < 90){
                          opacity = 0;
                        }

                        // intense bands
                        if(thermocycler.sequence[1].subSteps[0].temp >= 90){
                          opacity = 1;
                        }

                      // Annealing

                        // no bands
                        if(
                          (
                            thermocycler.sequence[1].subSteps[1].temp >= 0 &&
                            thermocycler.sequence[1].subSteps[1].temp <= 19
                          ) ||
                          (
                            thermocycler.sequence[1].subSteps[1].temp >= 90 &&
                            thermocycler.sequence[1].subSteps[1].temp <= 100
                          )
                        ){
                          opacity = 0;
                        }

                        // faint bands
                        if(
                          (
                            thermocycler.sequence[1].subSteps[1].temp >= 20 &&
                            thermocycler.sequence[1].subSteps[1].temp <= 50
                          ) ||
                          (
                            thermocycler.sequence[1].subSteps[1].temp >= 74 &&
                            thermocycler.sequence[1].subSteps[1].temp <= 89
                          )
                        ){
                          opacity = 0.5;
                        }

                        // intense bands
                        if(
                          thermocycler.sequence[1].subSteps[1].temp >= 51 &&
                          thermocycler.sequence[1].subSteps[1].temp <= 73
                        ){
                          opacity = 1;
                        }

                      // Elongation

                        // no bands
                        if(
                          (
                            thermocycler.sequence[1].subSteps[2].temp >= 0 &&
                            thermocycler.sequence[1].subSteps[2].temp <= 50
                          ) ||
                          (
                            thermocycler.sequence[1].subSteps[2].temp >= 77 &&
                            thermocycler.sequence[1].subSteps[2].temp <= 100
                          )
                        ){
                          opacity = 0;
                        }

                        if(
                          (
                            thermocycler.sequence[1].subSteps[2].time >= 0 &&
                            thermocycler.sequence[1].subSteps[2].time <= 19
                          )
                        ){
                          opacity = 0;
                        }

                        // very faint bands

                        if(
                          (
                            thermocycler.sequence[1].subSteps[2].time >= 20 &&
                            thermocycler.sequence[1].subSteps[2].time <= 45
                          )
                        ){
                          opacity = 0.2;
                        }

                        // faint bands
                        if(
                          (
                            thermocycler.sequence[1].subSteps[2].temp >= 50 &&
                            thermocycler.sequence[1].subSteps[2].temp <= 70
                          )
                        ){
                          opacity = 0.5;
                        }

                        if(
                          (
                            thermocycler.sequence[1].subSteps[2].time >= 46 &&
                            thermocycler.sequence[1].subSteps[2].time <= 59
                          )
                        ){
                          opacity = 0.5;
                        }

                        // intense bands
                        if(
                          (
                            thermocycler.sequence[1].subSteps[2].temp >= 71 &&
                            thermocycler.sequence[1].subSteps[2].temp <= 77
                          )
                        ){
                          opacity = 1;
                        }

                        if(
                          (
                            thermocycler.sequence[1].subSteps[2].time >= 60
                          )
                        ){
                          opacity = 1;
                        }

                    }

                    // Lane conditions for banding data
                    if(solution.solutions["DNA A"] >= 1){
                      item.cellItems.push({
                        "gelId": id,
                        "gelType": "DRAGGABLE",
                        "height": 100,
                        "opacity": opacity,
                        "value": 240,
                        "selected": false
                      })
                      id++;
                    }
                    // Lane conditions for banding data
                    if(solution.solutions["DNA B"] >= 1){
                      item.cellItems.push({
                        "gelId": id,
                        "gelType": "DRAGGABLE",
                        "height": 100,
                        "opacity": opacity,
                        "value": 240,
                        "selected": false
                      })
                      id++;
                    }
                    // Lane conditions for banding data
                    if(solution.solutions["DNA C"] >= 1){
                      item.cellItems.push({
                        "gelId": id,
                        "gelType": "DRAGGABLE",
                        "height": 100,
                        "opacity": opacity,
                        "value": 240,
                        "selected": false
                      })
                      id++;
                      item.cellItems.push({
                        "gelId": id,
                        "gelType": "DRAGGABLE",
                        "height": 100,
                        "opacity": opacity,
                        "value": 240,
                        "selected": false
                      })
                      id++;
                    }

                    if(solution.solutions["TAS2R38 taster allele control DNA"] >= 1){
                      item.cellItems.push({
                        "gelId": id,
                        "gelType": "DRAGGABLE",
                        "height": 100,
                        "opacity": opacity,
                        "value": 240,
                        "selected": false
                      })
                      id++;
                    }

                    if(solution.solutions["TAS2R38 non-taster allele control DNA"] >= 1){
                      item.cellItems.push({
                        "gelId": id,
                        "gelType": "DRAGGABLE",
                        "height": 100,
                        "opacity": opacity,
                        "value": 240,
                        "selected": false
                      })
                      id++;
                    }

                    // These have no result
                    //if(solution.solutions["dH20"] > 1){}
                    //if(solution.solutions["Prokaryotic Control DNA"] >= 1){}
                  }

               }
               return item;
           });
      }

      return col;
    });

    return data;
  }

  // getIdealDescription = () => {

  //   let solutionIDs = {
  //     "S": 4,
  //     "C1": 1,
  //     "C2": 2,
  //     "C3": 3,
  //     "Extracted human DNA": 13
  //   }
  //   let varSolution = "";
  //   let solObject = this.props.interactionProps.solutions[solutionIDs["Extracted human DNA"] - 1].solutions;

  //   if(solObject !== null){
  //     varSolution = Object.keys(solObject)[0];
  //   }

  //   let description = "";

  //   switch(varSolution) {
  //     case "DNA A":
  //       // code block
  //       description = "The sample lane should have one band that is approximately 1000 bp. The single band that appears in the control 1 lane should appear at the same position as the 1000bp mark on the DNA ladder. The control 2 lane should line up to approximately 700 bp. The control 3 and control 4 lanes should appear blank.";
  //       break;
  //     case "DNA B":
  //       // code block
  //       description = "The sample lane should have one band that is approximately 700 bp. The single band that appears in the control 1 lane should appear at the same position as the 1000bp mark on the DNA ladder. The control 2 lane should line up to approximately 700 bp. The control 3 and control 4 lanes should appear blank.";
  //       break;
  //     case "DNA C":
  //       // code block
  //       description = "The sample lane should have two bands. One band that is approximately 1000 bp and another band that is approximately 700 bp. The single band that appears in the control 1 lane should appear at the same position as the 1000bp mark on the DNA ladder. The control 2 lane should line up to approximately 700 bp. The control 3 and control 4 lanes should appear blank.";
  //       break;
  //     default:
  //       // code block
  //   }

  //   return description;
  // }

  render() {
    return (
      <ResultComparison
        altText={"Three gels are depicted, these are your predicted results from the question in the predictions section, your actual results, and your ideal results."}
        predictedState={{
          type: "component",
          altText: "Predicted results.",
          component: (
            <>
              <p>These are your initial predictions:</p>
              <GelDragDrop
                {...this.props.gelDragDropPrediction}
                useAbbrevLabel={true}
                resultComparison={true}
              />
            </>
          ),
        }}
        actualState={{
          type: "component",
          altText: "Actual results.",
          component: (
            <>
              <p>These are your actual results:</p>
              <GelDragDrop
                {...this.getActualLanes(cloneDeep(params))}
                resultComparison={true}
              />
            </>
          ),
        }}
        idealState={{
          type: "component",
          altText: "Ideal results.",
          component: (
            <>
              <p>These are your ideal results:</p>
              <GelDragDrop
                {...this.getIdealLanes(cloneDeep(params))}
                resultComparison={true}
              />
              {/*<p>
                {this.getIdealDescription()}
              </p>*/}
            </>
          ),
        }}
      />
    );
  }
}
